// Conversations — every chat thread in the organisation, read-only.
//
// Org admins can read what their users asked and what the agents answered: a
// compliance decision the organisation made, so this page is plain about it
// rather than hiding it. Per-run cost and tool detail stay in Audit logs.

import { useQuery } from "@tanstack/react-query";
import { Bot, Search } from "lucide-react";
import * as React from "react";

import { Markdown } from "@/components/chat/markdown";
import { PageHeader } from "@/components/layout-bits";
import { Card, Input, Skeleton } from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { getOrgConversation, listOrgConversations } from "@/lib/api/chat";
import { qk } from "@/lib/query/keys";
import { cn, formatDate } from "@/lib/utils";

export default function ConversationsPage() {
  const [email, setEmail] = React.useState("");
  const [applied, setApplied] = React.useState("");
  const [selected, setSelected] = React.useState<string | null>(null);
  const list = useQuery({
    queryKey: qk.orgConversations(applied),
    queryFn: () => listOrgConversations(applied || undefined),
  });

  return (
    <div>
      <PageHeader
        title="Conversations"
        description="Every chat in your organisation. Only org admins can see this page."
      />
      <form
        className="mb-4 flex max-w-md gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(email.trim());
          setSelected(null);
        }}
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Filter by user email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Filter by user email"
          />
        </div>
      </form>

      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (list.data ?? []).length === 0 ? (
        <EmptyState
          title="No conversations"
          hint={applied ? `Nothing from ${applied}.` : "Nobody has chatted with an agent yet."}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <Card className="max-h-[70vh] overflow-y-auto p-2">
            {(list.data ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelected(c.id)}
                className={cn(
                  "block w-full rounded-md px-3 py-2 text-left hover:bg-muted",
                  selected === c.id && "bg-muted",
                )}
              >
                <div className="truncate text-sm font-medium">{c.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {c.user_email} · {c.agent_name} · {formatDate(c.updated_at)}
                  {c.is_evaluation ? " · evaluation" : ""}
                </div>
              </button>
            ))}
          </Card>
          <Card className="max-h-[70vh] overflow-y-auto p-4">
            {selected ? (
              <Thread id={selected} />
            ) : (
              <p className="text-sm text-muted-foreground">Select a conversation to read it.</p>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function Thread({ id }: { id: string }) {
  const q = useQuery({ queryKey: qk.orgConversation(id), queryFn: () => getOrgConversation(id) });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading || !q.data) return <Skeleton className="h-40 w-full" />;
  return (
    <div className="space-y-4">
      <div className="border-b border-border pb-2">
        <div className="font-medium">{q.data.title}</div>
        <div className="text-xs text-muted-foreground">
          {q.data.user_email} with {q.data.agent_name}
          {q.data.is_evaluation ? " (evaluation session)" : ""}
        </div>
      </div>
      {q.data.messages.map((m) =>
        m.role === "user" ? (
          <div key={m.id} className="flex justify-end">
            <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-muted px-4 py-2 text-sm">
              {m.content}
            </div>
          </div>
        ) : (
          <div key={m.id} className="flex gap-2">
            <Bot className="mt-1 h-4 w-4 shrink-0 text-primary" />
            {m.status === "complete" ? (
              <Markdown text={m.content} />
            ) : (
              <span className="text-sm text-muted-foreground">
                {m.status === "pending" ? "Reply in progress…" : m.content}
              </span>
            )}
          </div>
        ),
      )}
    </div>
  );
}
